/**
 * Achievement Poster Generator - Admin Panel
 * Premium "Congrats Card" template — refined per user feedback:
 *   - Fixed export black-space bug (scale moved off ref'd element)
 *   - Black GRRAS logo (works on cream background)
 *   - Restrained leaf count for cleaner premium look
 *   - Photo zoom + position controls (poor man's crop)
 *   - Common template that works for ALL courses (10+ exams)
 */
import React, { useRef, useState, useMemo } from 'react';
import { toPng } from 'html-to-image';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Award, Download, Upload, X, Copy, FileText, User, Camera, Move, ZoomIn, Sparkles, Loader2 } from 'lucide-react';
import { CERTIFICATE_TEMPLATES, COURSE_KEYS } from './certificateTemplates';
import { API_BASE, getAdminToken } from './adminUtils';

const GRRAS_LOGO_URL = '/images/grras-logo.png'; // white transparent PNG — sits naturally on dark theme
const GRRAS_PHONE = '+91 9001991227';
const GRRAS_ADDRESS = 'A-81, Singh Bhoomi, Khatipura Road, Behind Marudhar Hospital, Jaipur';

const FORMATS = [
  { value: 'square',    label: 'Square 1080×1080',    w: 1080, h: 1080, desc: 'Instagram Post · WhatsApp Share · FB' },
  { value: 'story',     label: 'Story 1080×1920',     w: 1080, h: 1920, desc: 'WhatsApp Status · IG Story' },
  { value: 'landscape', label: 'Landscape 1200×628',  w: 1200, h: 628,  desc: 'LinkedIn · Twitter · FB Cover' }
];

// Default photo/cert transforms
const TRANSFORM_DEFAULT = { zoom: 1, x: 0, y: 0 };

// ---------- Drag-drop Image Uploader ----------
const ImageDropzone = ({ label, value, onChange, sublabel, testid, recommended }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please drop a valid image (JPG / PNG / WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image too large. Max 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => onChange(e.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-2 mb-2">
        {label}
        {recommended && <span className="text-[10px] normal-case tracking-normal text-amber-400">(recommended)</span>}
      </Label>
      {sublabel && <p className="text-[11px] text-slate-500 mb-2 -mt-1">{sublabel}</p>}
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="preview" className="w-32 h-32 object-cover rounded-lg border border-white/20" />
          <button
            type="button"
            onClick={() => onChange(null)}
            data-testid={`${testid}-clear`}
            className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            handleFile(e.dataTransfer.files?.[0]);
          }}
          onClick={() => inputRef.current?.click()}
          data-testid={testid}
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition ${
            drag ? 'border-amber-400 bg-amber-500/10' : 'border-white/20 hover:border-white/40 bg-white/5'
          }`}
        >
          <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
          <p className="text-xs text-slate-300">Drag &amp; drop or click to upload</p>
          <p className="text-[10px] text-slate-500 mt-1">JPG · PNG · WebP · max 5MB</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      )}
    </div>
  );
};

// ---------- Restrained SVG Leaves (less is more) ----------
const MAPLE_PATH = 'M50,8 L56,28 Q60,32 64,30 L78,18 L72,38 Q73,42 77,42 L92,42 L78,54 Q76,58 80,60 L88,72 L70,68 Q66,68 66,72 L62,90 L52,76 Q50,74 48,76 L38,90 L34,72 Q34,68 30,68 L12,72 L20,60 Q24,58 22,54 L8,42 L23,42 Q27,42 28,38 L22,18 L36,30 Q40,32 44,28 Z';
const OAK_PATH = 'M50,6 Q54,18 60,18 Q70,16 70,28 Q68,38 62,38 Q70,42 70,52 Q68,62 60,62 Q66,68 62,80 Q56,90 50,88 Q44,90 38,80 Q34,68 40,62 Q32,62 30,52 Q30,42 38,38 Q32,38 30,28 Q30,16 40,18 Q46,18 50,6 Z';

const Leaf = ({ d, color, x, y, size, rot, opacity = 1 }) => (
  <g transform={`translate(${x} ${y}) rotate(${rot}) scale(${size / 100})`} opacity={opacity}>
    <path d={d} fill={color} stroke="rgba(0,0,0,0.06)" strokeWidth={1} />
  </g>
);

const Leaves = ({ w, h }) => {
  // Restrained palette: just 5 leaves total, well-spaced for breathing room
  const C_RED = '#C84B31', C_ORANGE = '#E8702A', C_GOLD = '#E8A33A';
  const leaves = [
    // Top-left corner (light cluster)
    { d: MAPLE_PATH, color: C_RED,    x: -30,        y: -20,        size: w * 0.16, rot: -25, opacity: 0.85 },
    { d: OAK_PATH,   color: C_GOLD,   x: w * 0.08,   y: -40,        size: w * 0.10, rot: 30,  opacity: 0.55 },
    // Right edge mid
    { d: MAPLE_PATH, color: C_ORANGE, x: w - 60,     y: h * 0.45,   size: w * 0.13, rot: 70,  opacity: 0.65 },
    // Bottom-left corner
    { d: OAK_PATH,   color: C_RED,    x: -20,        y: h - 70,     size: w * 0.14, rot: 200, opacity: 0.80 },
    // Bottom-right (small accent)
    { d: MAPLE_PATH, color: C_GOLD,   x: w * 0.78,   y: h - 60,     size: w * 0.10, rot: 230, opacity: 0.55 }
  ];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}
         style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}
         xmlns="http://www.w3.org/2000/svg">
      {leaves.map((l, i) => <Leaf key={i} {...l} />)}
    </svg>
  );
};

// ---------- PREMIUM CONGRATS POSTER ----------
const CongratsPoster = ({ d, course, sizes }) => {
  const accentColor = course.color;
  const initials = (d.studentName || 'STUDENT').split(/\s+/).map(s => s[0]).join('').slice(0, 2).toUpperCase();
  const photoT = d.photoTransform || TRANSFORM_DEFAULT;
  const certT = d.certTransform || TRANSFORM_DEFAULT;

  return (
    <div style={{
      width: '100%', height: '100%', position: 'relative', overflow: 'hidden',
      background: 'linear-gradient(160deg, #0f0f1a 0%, #1a1426 40%, #2a1410 100%)',
      color: '#f5f1e8',
      fontFamily: '"Segoe UI", system-ui, -apple-system, Roboto, sans-serif',
      paddingBottom: `${sizes.footerH + sizes.pad * 0.3}px`,
      display: 'flex', flexDirection: 'column'
    }}>
      <Leaves w={sizes.w} h={sizes.h} />

      {/* Top-right logo — original white PNG, sits naturally on dark bg */}
      <div style={{
        position: 'absolute', top: `${sizes.pad * 0.85}px`, right: `${sizes.pad}px`, zIndex: 4
      }}>
        <img src={GRRAS_LOGO_URL} alt="Grras" crossOrigin="anonymous"
             style={{
               height: `${sizes.logoH}px`,
               width: 'auto',
               display: 'block',
               filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.4))'
             }} />
      </div>

      {/* CONGRATS ribbon */}
      <div style={{
        position: 'relative', zIndex: 3,
        marginTop: `${sizes.pad * 1.4}px`,
        display: 'flex', justifyContent: 'center'
      }}>
        <div style={{
          background: `linear-gradient(180deg, ${accentColor} 0%, ${shade(accentColor, -15)} 100%)`,
          color: '#fff',
          fontWeight: 900,
          fontSize: `${sizes.ribbonText}px`,
          letterSpacing: '0.22em',
          padding: `${sizes.gap * 0.75}px ${sizes.gap * 2.6}px`,
          textTransform: 'uppercase',
          boxShadow: `0 ${sizes.gap * 0.5}px ${sizes.gap}px rgba(200, 75, 49, 0.30), inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -2px 4px rgba(0,0,0,0.18)`,
          clipPath: 'polygon(3% 0%, 100% 0%, 97% 100%, 0% 100%)'
        }}>
          ✨ CONGRATS ✨
        </div>
      </div>

      {/* Student name */}
      <div style={{ position: 'relative', zIndex: 3, textAlign: 'center', marginTop: `${sizes.gap * 1.2}px`, padding: `0 ${sizes.pad}px` }}>
        <div style={{
          fontWeight: 900,
          fontSize: `${sizes.studentName}px`,
          lineHeight: 1.0,
          color: '#ffffff',
          letterSpacing: '-0.015em',
          textShadow: '0 2px 12px rgba(0,0,0,0.5)'
        }}>
          {(d.studentName || 'STUDENT NAME').toUpperCase()}
        </div>
        <div style={{
          display: 'inline-block',
          marginTop: `${sizes.gap * 0.7}px`,
          padding: `${sizes.gap * 0.35}px ${sizes.gap * 1.1}px`,
          background: `linear-gradient(180deg, ${accentColor}, ${shade(accentColor, -10)})`,
          color: '#fff',
          fontSize: `${sizes.courseBadge}px`,
          fontWeight: 800,
          letterSpacing: '0.08em',
          borderRadius: `${sizes.gap * 0.5}px`,
          textTransform: 'uppercase',
          boxShadow: `0 ${sizes.gap * 0.3}px ${sizes.gap}px ${accentColor}40`
        }}>
          {course.shortName}{course.code ? ` · ${course.code}` : ''}
        </div>
      </div>

      {/* Photo + Certificate */}
      <div style={{
        position: 'relative', zIndex: 3,
        display: 'flex',
        gap: `${sizes.gap * 1.4}px`,
        justifyContent: 'center',
        alignItems: 'stretch',
        marginTop: `${sizes.gap * 1.4}px`,
        marginBottom: `${sizes.gap * 1.2}px`,
        padding: `0 ${sizes.pad * 0.6}px`
      }}>
        {/* Student photo with zoom + pan transform */}
        <div style={{
          width: `${sizes.photoBox}px`,
          height: `${sizes.photoBox}px`,
          borderRadius: `${sizes.gap * 0.7}px`,
          overflow: 'hidden',
          background: d.photo ? '#fff' : `linear-gradient(135deg, ${accentColor}, ${shade(accentColor, -25)})`,
          boxShadow: `0 ${sizes.gap * 0.8}px ${sizes.gap * 1.6}px rgba(70, 30, 10, 0.20), inset 0 0 0 ${sizes.gap * 0.15}px #fff`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          transform: 'rotate(-1.5deg)',
          position: 'relative'
        }}>
          {d.photo ? (
            <img
              src={d.photo}
              alt=""
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover',
                display: 'block',
                transform: `scale(${photoT.zoom}) translate(${photoT.x}%, ${photoT.y}%)`,
                transformOrigin: 'center'
              }}
            />
          ) : (
            <span style={{ fontSize: `${sizes.photoBox * 0.32}px`, fontWeight: 900, color: '#fff' }}>{initials}</span>
          )}
        </div>

        {/* Certificate */}
        <div style={{
          width: `${sizes.certBox}px`,
          height: `${sizes.photoBox}px`,
          borderRadius: `${sizes.gap * 0.7}px`,
          overflow: 'hidden',
          background: '#fff',
          boxShadow: `0 ${sizes.gap * 0.8}px ${sizes.gap * 1.6}px rgba(70, 30, 10, 0.20), inset 0 0 0 ${sizes.gap * 0.15}px #fff`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          transform: 'rotate(1.5deg)'
        }}>
          {d.certificate ? (
            <img
              src={d.certificate}
              alt=""
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover',
                display: 'block',
                transform: `scale(${certT.zoom}) translate(${certT.x}%, ${certT.y}%)`,
                transformOrigin: 'center'
              }}
            />
          ) : (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: `${sizes.gap}px` }}>
              <div style={{ fontSize: `${sizes.gap * 2.5}px`, marginBottom: `${sizes.gap * 0.4}px` }}>📜</div>
              <div style={{ fontSize: `${sizes.gap * 0.85}px`, fontWeight: 600, color: '#64748b' }}>
                Upload certificate<br/>image
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Caption */}
      <div style={{
        position: 'relative', zIndex: 3,
        textAlign: 'center',
        flex: 1,
        display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: `${sizes.gap * 0.85}px`,
        padding: `0 ${sizes.pad * 0.8}px`
      }}>
        <div style={{
          fontSize: `${sizes.captionMain}px`, fontWeight: 600, lineHeight: 1.32, color: '#f5f1e8',
          maxWidth: '92%', margin: '0 auto'
        }}>
          {d.achievementLine || (
            <>
              {d.studentName || 'Our student'} has aced the{' '}
              <span style={{ color: '#fbbf24', fontWeight: 800 }}>{course.shortName}</span> certification
              {d.score && <> with a <span style={{ fontWeight: 800, borderBottom: `${sizes.gap * 0.18}px solid #fbbf24`, paddingBottom: '2px' }}>{d.score}!</span></>}
              {!d.score && <>!</>}
            </>
          )}
        </div>

        <div style={{
          width: `${sizes.gap * 5}px`, height: `${sizes.gap * 0.18}px`,
          background: `linear-gradient(90deg, transparent, #fbbf24, transparent)`,
          margin: '0 auto', borderRadius: '2px'
        }} />

        <div style={{ fontSize: `${sizes.captionSub}px`, fontWeight: 500, color: '#e2dccd', lineHeight: 1.4 }}>
          Proud moment for <span style={{ color: '#fbbf24', fontWeight: 800 }}>Grras Solutions!</span><br/>
          Your dedication truly inspires
        </div>

        <div style={{ fontSize: `${sizes.captionSub}px`, fontWeight: 500, color: '#e2dccd', lineHeight: 1.4 }}>
          Keep shining and reaching new heights! 🚀
        </div>

        <div style={{ fontSize: `${sizes.captionSub}px`, fontWeight: 500, color: '#e2dccd', lineHeight: 1.4 }}>
          <span style={{ color: '#fbbf24', fontWeight: 800 }}>Best Wishes,</span><br/>
          Team Grras Solutions.
        </div>
      </div>

      {/* Bottom red strip — 2-row layout: phone on top, address below */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 5,
        background: `linear-gradient(180deg, ${accentColor} 0%, ${shade(accentColor, -18)} 100%)`,
        color: '#fff',
        padding: `${sizes.gap * 0.7}px ${sizes.pad * 0.6}px`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: `${sizes.gap * 0.25}px`,
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: `${sizes.footerText}px`, fontWeight: 800, letterSpacing: '0.02em',
          display: 'inline-flex', alignItems: 'center', gap: '8px'
        }}>
          📞 Call {GRRAS_PHONE}
        </div>
        <div style={{
          fontSize: `${sizes.footerText * 0.78}px`, fontWeight: 500, opacity: 0.95,
          lineHeight: 1.3, padding: `0 ${sizes.gap * 0.5}px`
        }}>
          📍 {GRRAS_ADDRESS}
        </div>
      </div>
    </div>
  );
};

// shade utility — darken/lighten a hex color
function shade(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + Math.round(255 * (percent / 100))));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + Math.round(255 * (percent / 100))));
  const b = Math.max(0, Math.min(255, (num & 0xff) + Math.round(255 * (percent / 100))));
  return `rgb(${r}, ${g}, ${b})`;
}

// ---------- SIZE PRESETS ----------
function getSizes(format) {
  if (format === 'square') {
    return {
      w: 1080, h: 1080, pad: 56, gap: 26, footerH: 78,
      logoH: 110, ribbonText: 38, courseBadge: 26,
      studentName: 86,
      photoBox: 350, certBox: 460,
      captionMain: 32, captionSub: 26,
      footerText: 22
    };
  }
  if (format === 'story') {
    return {
      w: 1080, h: 1920, pad: 70, gap: 32, footerH: 100,
      logoH: 130, ribbonText: 46, courseBadge: 30,
      studentName: 110,
      photoBox: 420, certBox: 540,
      captionMain: 40, captionSub: 32,
      footerText: 26
    };
  }
  return {
    w: 1200, h: 628, pad: 30, gap: 14, footerH: 50,
    logoH: 56, ribbonText: 22, courseBadge: 14,
    studentName: 50,
    photoBox: 200, certBox: 260,
    captionMain: 18, captionSub: 16,
    footerText: 14
  };
}

// ---------- MAIN COMPONENT ----------
export const AchievementPosterTab = () => {
  const posterRef = useRef(null);
  const [format, setFormat] = useState('square');
  const [exporting, setExporting] = useState(false);

  const [data, setData] = useState({
    studentName: '',
    courseKey: 'RHCSA',
    customCourseName: '',
    score: '',
    achievementLine: '',
    photo: null,
    certificate: null,
    photoTransform: { ...TRANSFORM_DEFAULT },
    certTransform: { ...TRANSFORM_DEFAULT }
  });

  const update = (k, v) => setData(prev => ({ ...prev, [k]: v }));
  const updatePhotoT = (k, v) => setData(prev => ({ ...prev, photoTransform: { ...prev.photoTransform, [k]: v } }));
  const resetPhotoT = () => setData(prev => ({ ...prev, photoTransform: { ...TRANSFORM_DEFAULT } }));
  const updateCertT = (k, v) => setData(prev => ({ ...prev, certTransform: { ...prev.certTransform, [k]: v } }));
  const resetCertT = () => setData(prev => ({ ...prev, certTransform: { ...TRANSFORM_DEFAULT } }));

  // AI caption suggestions
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const fetchSuggestions = async () => {
    if (!data.studentName.trim()) {
      alert('Please enter the student name first');
      return;
    }
    const token = getAdminToken();
    if (!token) return;
    setLoadingSuggestions(true);
    setSuggestions(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/poster/suggest-captions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          student_name: data.studentName,
          course_short: course.shortName,
          course_full: course.fullName || course.shortName,
          score: data.score || null
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setSuggestions(json);
    } catch (e) {
      alert(`Suggestion failed: ${e.message}`);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const course = useMemo(() => {
    const base = CERTIFICATE_TEMPLATES[data.courseKey] || CERTIFICATE_TEMPLATES.CUSTOM;
    if (data.courseKey === 'CUSTOM') {
      return { ...base, fullName: data.customCourseName || 'Custom Achievement', shortName: data.customCourseName?.split(' ')[0]?.toUpperCase() || 'CUSTOM' };
    }
    return base;
  }, [data.courseKey, data.customCourseName]);

  const dims = FORMATS.find(f => f.value === format) || FORMATS[0];
  const sizes = useMemo(() => getSizes(format), [format]);
  const previewScale = Math.min(1, 520 / dims.w, 760 / dims.h);

  const handleExport = async () => {
    if (!posterRef.current) return;
    if (!data.studentName.trim()) {
      alert('Please enter the student name first');
      return;
    }
    setExporting(true);
    try {
      const imgs = Array.from(posterRef.current.querySelectorAll('img'));
      await Promise.all(imgs.map(img => {
        if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
        return new Promise(resolve => {
          img.onload = resolve;
          img.onerror = resolve;
          setTimeout(resolve, 3000);
        });
      }));
      const dataUrl = await toPng(posterRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#0f0f1a',
        width: dims.w,
        height: dims.h
      });
      if (!dataUrl || dataUrl === 'data:,') throw new Error('Empty image');
      const link = document.createElement('a');
      const safeName = (data.studentName || 'student').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      link.download = `grras-${course.shortName.toLowerCase()}-${safeName}-${format}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error(e);
      alert(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  const copyCaption = () => {
    const courseLine = `${course.shortName}${course.code ? ` (${course.code})` : ''}`;
    const scoreLine = data.score ? ` with a ${data.score}` : '';
    const text = `🎉 CONGRATS ${data.studentName || 'Our student'}!\n\n✨ Cleared ${courseLine} — ${course.fullName}${scoreLine}!\n\nProud moment for Grras Solutions! Your dedication truly inspires. Keep shining and reaching new heights! 🚀\n\nBest Wishes,\nTeam Grras Solutions.\n\n📞 Call ${GRRAS_PHONE}\n📍 ${GRRAS_ADDRESS}\n\n#${course.shortName.replace(/\s+/g, '')} #${course.vendor.replace(/\s+/g, '')} #GrrasSolutions #StudentSuccess #Jaipur`;
    navigator.clipboard.writeText(text);
    alert('Caption copied! Paste on WhatsApp / Insta / FB / LinkedIn.');
  };

  return (
    <div className="space-y-6" data-testid="achievement-poster-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Award className="w-6 h-6 text-amber-400" />
            Student Achievement Poster
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Premium "Congrats" cards · works for all courses · WhatsApp · Insta · FB · LinkedIn ready.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Form */}
        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Format</Label>
              <div className="flex flex-wrap gap-2">
                {FORMATS.map(f => (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setFormat(f.value)}
                    data-testid={`fmt-${f.value}`}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition ${
                      format === f.value ? 'bg-amber-500 text-black' : 'bg-white/10 text-slate-300 hover:bg-white/20'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">{dims.desc}</p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-2 mb-2">
                <User className="w-3 h-3" /> Student Name <span className="text-red-400">*</span>
              </Label>
              <Input
                data-testid="poster-student-name"
                value={data.studentName}
                onChange={(e) => update('studentName', e.target.value)}
                placeholder="e.g. Nitesh Golada"
                className="bg-white/10 border-white/20 text-white"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Course / Exam</Label>
              <select
                data-testid="poster-course-select"
                value={data.courseKey}
                onChange={(e) => update('courseKey', e.target.value)}
                className="w-full bg-white/10 border border-white/20 text-white rounded-md px-3 py-2 text-sm"
              >
                {COURSE_KEYS.map(k => (
                  <option key={k} value={k} className="bg-slate-800">
                    {CERTIFICATE_TEMPLATES[k].shortName}
                    {CERTIFICATE_TEMPLATES[k].fullName ? ` — ${CERTIFICATE_TEMPLATES[k].fullName}` : ''}
                  </option>
                ))}
              </select>
              {data.courseKey === 'CUSTOM' && (
                <Input
                  data-testid="poster-custom-course"
                  value={data.customCourseName}
                  onChange={(e) => update('customCourseName', e.target.value)}
                  placeholder="Custom course name"
                  className="mt-2 bg-white/10 border-white/20 text-white"
                />
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Score / Highlight (optional)</Label>
              <Input
                data-testid="poster-score"
                value={data.score}
                onChange={(e) => update('score', e.target.value)}
                placeholder="e.g. perfect score / 245/300 / 98%"
                className="bg-white/10 border-white/20 text-white"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Custom Achievement Line (optional)</Label>
                <button
                  type="button"
                  onClick={fetchSuggestions}
                  disabled={loadingSuggestions || !data.studentName.trim()}
                  data-testid="poster-suggest-btn"
                  className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md bg-gradient-to-r from-amber-500/20 to-fuchsia-500/20 hover:from-amber-500/30 hover:to-fuchsia-500/30 border border-amber-400/30 text-amber-200 disabled:opacity-50"
                >
                  {loadingSuggestions ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  {loadingSuggestions ? 'Generating…' : 'AI Suggest'}
                </button>
              </div>
              <Textarea
                data-testid="poster-achievement-line"
                value={data.achievementLine}
                onChange={(e) => update('achievementLine', e.target.value)}
                rows={2}
                placeholder='Leave blank for auto-generated text · or click "AI Suggest" for 3 styled options'
                className="bg-white/10 border-white/20 text-white text-sm"
              />
              {suggestions && (
                <div className="mt-2 space-y-2" data-testid="poster-suggestions">
                  {[
                    { key: 'formal',        label: 'Formal',        emoji: '🎯', cls: 'bg-sky-500/10 border-sky-400/30 hover:bg-sky-500/20',           tcls: 'text-sky-300' },
                    { key: 'celebratory',   label: 'Celebratory',   emoji: '🎉', cls: 'bg-amber-500/10 border-amber-400/30 hover:bg-amber-500/20',     tcls: 'text-amber-300' },
                    { key: 'inspirational', label: 'Inspirational', emoji: '🚀', cls: 'bg-fuchsia-500/10 border-fuchsia-400/30 hover:bg-fuchsia-500/20', tcls: 'text-fuchsia-300' }
                  ].map(({ key, label, emoji, cls, tcls }) => suggestions[key] ? (
                    <button
                      key={key}
                      type="button"
                      onClick={() => update('achievementLine', suggestions[key])}
                      data-testid={`suggestion-${key}`}
                      className={`w-full text-left p-2 rounded-md border transition ${cls}`}
                    >
                      <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${tcls}`}>{emoji} {label}</div>
                      <div className="text-xs text-slate-200 leading-snug">{suggestions[key]}</div>
                    </button>
                  ) : null)}
                  <button
                    type="button"
                    onClick={() => setSuggestions(null)}
                    className="text-[10px] text-slate-400 hover:text-white underline"
                  >
                    Clear suggestions
                  </button>
                </div>
              )}
            </div>

            {/* Image uploads */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <ImageDropzone
                label={<><Camera className="w-3 h-3 inline mr-1" />Student Photo</>}
                value={data.photo}
                onChange={(v) => { update('photo', v); resetPhotoT(); }}
                testid="dz-photo"
                recommended
              />
              <ImageDropzone
                label={<><FileText className="w-3 h-3 inline mr-1" />Certificate Image</>}
                value={data.certificate}
                onChange={(v) => { update('certificate', v); resetCertT(); }}
                testid="dz-certificate"
                recommended
              />
            </div>

            {/* Photo zoom + position controls */}
            {data.photo && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-amber-300 font-semibold flex items-center gap-2">
                    <ZoomIn className="w-3 h-3" /> Photo Adjust
                  </Label>
                  <button
                    type="button"
                    onClick={resetPhotoT}
                    data-testid="poster-photo-reset"
                    className="text-[10px] text-slate-400 hover:text-white underline"
                  >
                    Reset
                  </button>
                </div>
                <div>
                  <div className="flex items-center justify-between text-[11px] text-slate-300 mb-1">
                    <span>🔍 Zoom</span><span>{data.photoTransform.zoom.toFixed(2)}×</span>
                  </div>
                  <input
                    type="range" min="0.5" max="2.5" step="0.05"
                    value={data.photoTransform.zoom}
                    onChange={(e) => updatePhotoT('zoom', parseFloat(e.target.value))}
                    data-testid="poster-photo-zoom"
                    className="w-full accent-amber-500"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between text-[11px] text-slate-300 mb-1">
                    <span>↔️ Horizontal</span><span>{data.photoTransform.x}%</span>
                  </div>
                  <input
                    type="range" min="-50" max="50" step="1"
                    value={data.photoTransform.x}
                    onChange={(e) => updatePhotoT('x', parseInt(e.target.value, 10))}
                    data-testid="poster-photo-x"
                    className="w-full accent-amber-500"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between text-[11px] text-slate-300 mb-1">
                    <span>↕️ Vertical</span><span>{data.photoTransform.y}%</span>
                  </div>
                  <input
                    type="range" min="-50" max="50" step="1"
                    value={data.photoTransform.y}
                    onChange={(e) => updatePhotoT('y', parseInt(e.target.value, 10))}
                    data-testid="poster-photo-y"
                    className="w-full accent-amber-500"
                  />
                </div>
                <p className="text-[10px] text-slate-500 italic flex items-center gap-1">
                  <Move className="w-3 h-3" /> Adjust to crop face/upper-body into the frame
                </p>
              </div>
            )}

            {/* Certificate zoom + position controls */}
            {data.certificate && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-amber-300 font-semibold flex items-center gap-2">
                    <ZoomIn className="w-3 h-3" /> Certificate Adjust
                  </Label>
                  <button
                    type="button"
                    onClick={resetCertT}
                    data-testid="poster-cert-reset"
                    className="text-[10px] text-slate-400 hover:text-white underline"
                  >
                    Reset
                  </button>
                </div>
                <div>
                  <div className="flex items-center justify-between text-[11px] text-slate-300 mb-1">
                    <span>🔍 Zoom</span><span>{data.certTransform.zoom.toFixed(2)}×</span>
                  </div>
                  <input
                    type="range" min="0.5" max="2.5" step="0.05"
                    value={data.certTransform.zoom}
                    onChange={(e) => updateCertT('zoom', parseFloat(e.target.value))}
                    data-testid="poster-cert-zoom"
                    className="w-full accent-amber-500"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between text-[11px] text-slate-300 mb-1">
                    <span>↔️ Horizontal</span><span>{data.certTransform.x}%</span>
                  </div>
                  <input
                    type="range" min="-50" max="50" step="1"
                    value={data.certTransform.x}
                    onChange={(e) => updateCertT('x', parseInt(e.target.value, 10))}
                    data-testid="poster-cert-x"
                    className="w-full accent-amber-500"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between text-[11px] text-slate-300 mb-1">
                    <span>↕️ Vertical</span><span>{data.certTransform.y}%</span>
                  </div>
                  <input
                    type="range" min="-50" max="50" step="1"
                    value={data.certTransform.y}
                    onChange={(e) => updateCertT('y', parseInt(e.target.value, 10))}
                    data-testid="poster-cert-y"
                    className="w-full accent-amber-500"
                  />
                </div>
                <p className="text-[10px] text-slate-500 italic flex items-center gap-1">
                  <Move className="w-3 h-3" /> Crop to focus on the certificate seal/logo
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10">
              <Button
                onClick={handleExport}
                disabled={exporting || !data.studentName.trim()}
                data-testid="poster-export-btn"
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting…' : 'Download PNG'}
              </Button>
              <Button
                variant="outline"
                onClick={copyCaption}
                data-testid="poster-copy-caption"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Caption
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* RIGHT: Live preview (sticky on desktop so it stays visible while form scrolls) */}
        <div className="lg:sticky lg:top-4 lg:self-start">
        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Live Preview</span>
              <span className="text-[10px] text-slate-500">{dims.w}×{dims.h}</span>
            </div>
            <div className="bg-black/40 rounded-lg p-3 overflow-auto flex items-center justify-center" style={{ minHeight: '500px' }}>
              {/* Scaled wrapper — scale is OUTSIDE the ref so html-to-image captures full size */}
              <div style={{
                width: `${dims.w * previewScale}px`,
                height: `${dims.h * previewScale}px`,
                position: 'relative'
              }}>
                <div style={{
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top left',
                  width: `${dims.w}px`,
                  height: `${dims.h}px`
                }}>
                  <div
                    ref={posterRef}
                    data-testid="poster-canvas"
                    style={{ width: `${dims.w}px`, height: `${dims.h}px` }}
                  >
                    <CongratsPoster d={data} course={course} sizes={sizes} />
                  </div>
                </div>
              </div>
            </div>
            <p className="text-[10px] text-slate-500 mt-2 italic">
              Preview is scaled. Actual export will be {dims.w}×{dims.h} at 2× retina ({dims.w * 2}×{dims.h * 2} px).
            </p>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default AchievementPosterTab;
